import React, { useState, useEffect, useMemo, useRef } from 'react';
import SemanticGPUCard from './SemanticGPUCard';
import GPURecommendations from './GPURecommendations';
import QuotaRequestModal from './QuotaRequestModal';
import {
  SemanticGPUResponse,
  SemanticFilters,
  GPURecommendationRequest,
  SemanticGPUBrowseProps,
  GPUCatalogSummary,
  LocalFilters,
  SortConfig,
} from '../../types/gpu';
import { fetchWithAuth } from '../../utils/api';

// Types are now imported from ../../types/gpu

const DEFAULT_FILTERS: SemanticFilters = {
  performance_categories: [],
  memory_tiers: [],
  cost_tiers: [],
  use_cases: [],
  providers: [],
  regions: [],
  sort_by: 'performance_score',
  sort_order: 'desc'
};

const getProviderIcon = (provider: string) => {
  const className = "w-5 h-5";

  switch (provider.toLowerCase()) {
    case 'aws':
      return (
        <img
          src="/icons/icons8-aws-48.svg"
          alt="AWS"
          className={className}
        />
      );
    case 'gcp':
      return (
        <img
          src="/icons/icons8-google-cloud-48.svg"
          alt="Google Cloud"
          className={className}
        />
      );
    case 'azure':
      return (
        <img
          src="/icons/azure.svg"
          alt="Microsoft Azure"
          className={className}
        />
      );
    case 'oci':
      return (
        <img
          src="/icons/icons8-oracle-48.svg"
          alt="Oracle Cloud"
          className={className}
        />
      );
    case 'coreweave':
      return (
        <img
          src="/icons/icons8-coreweave-48.svg"
          alt="CoreWeave"
          className={className}
        />
      );
    default:
      return (
        <div className={`${className} bg-gray-600 rounded flex items-center justify-center text-white text-xs font-bold`}>
          {provider.charAt(0).toUpperCase()}
        </div>
      );
  }
};

export default function SemanticGPUBrowse({ onLaunchGPU, onViewDetails }: SemanticGPUBrowseProps) {
  const [gpus, setGpus] = useState<SemanticGPUResponse[]>([]);
  const [recommendations, setRecommendations] = useState<SemanticGPUResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [recommendationLoading, setRecommendationLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters] = useState<SemanticFilters>(DEFAULT_FILTERS);
  const [viewMode, setViewMode] = useState<'browse' | 'recommend'>('browse');
  const [displayMode, setDisplayMode] = useState<'expanded' | 'compact'>('compact');
  const [catalogSummary, setCatalogSummary] = useState<GPUCatalogSummary | null>(null);

  // Local table filters (separate from API filters)
  const [localFilters, setLocalFilters] = useState<LocalFilters>({
    provider: '',
    gpuName: '',
    performance: '',
    memoryMin: '',
    priceMax: '',
  });

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: '',
    direction: 'asc'
  });

  const [showQuotaRequestModal, setShowQuotaRequestModal] = useState(false);

  // Tooltip state for table row hover
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredGpu, setHoveredGpu] = useState<{
    gpu: SemanticGPUResponse;
    rect: DOMRect;
  } | null>(null);

  // Fetch semantic GPU catalog
  const fetchGPUs = async (currentFilters = filters) => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const queryParams = new URLSearchParams({
        limit: '0',
        offset: '0',
        include_stale: 'false'
      });

      // Convert filters to JSON string
      const filterJson = JSON.stringify({
        performance_categories: currentFilters.performance_categories,
        memory_tiers: currentFilters.memory_tiers,
        cost_tiers: currentFilters.cost_tiers,
        use_cases: currentFilters.use_cases,
        providers: currentFilters.providers,
        regions: currentFilters.regions,
        min_memory_gb: currentFilters.min_memory_gb,
        max_price_per_hour: currentFilters.max_price_per_hour,
        sort_by: currentFilters.sort_by,
        sort_order: currentFilters.sort_order
      });

      queryParams.append('filters', filterJson);

      const response = await fetchWithAuth(`/api/gpu/discover?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch GPUs: ${response.status}`);
      }

      const data: SemanticGPUResponse[] = await response.json();
      setGpus(data);

    } catch (err) {
      const error = err as Error;
      console.error('Error fetching semantic GPUs:', error);
      setError(error.message || 'Failed to fetch GPU catalog');
      setGpus([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch GPU recommendations
  const handleGetRecommendations = async (request: GPURecommendationRequest) => {
    try {
      setRecommendationLoading(true);
      setError(null);

      const response = await fetchWithAuth('/api/gpu/recommend', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`Failed to get recommendations: ${response.status}`);
      }

      const data = await response.json();
      setRecommendations(data);

    } catch (err: any) {
      console.error('Error getting recommendations:', err);
      setError(err.message || 'Failed to get recommendations');
      setRecommendations([]);
    } finally {
      setRecommendationLoading(false);
    }
  };

  // Fetch catalog summary
  const fetchCatalogSummary = async () => {
    try {
      const response = await fetchWithAuth('/api/gpu/catalog/summary');

      if (response.ok) {
        const summary: GPUCatalogSummary = await response.json();
        setCatalogSummary(summary);
      }
    } catch (err) {
      console.error('Error fetching catalog summary:', err);
    }
  };

  // Handle local filtering and sorting
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleLocalFilter = (column: string, value: string) => {
    setLocalFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  // Clear all local filters
  const clearAllLocalFilters = () => {
    setLocalFilters({
      provider: '',
      gpuName: '',
      performance: '',
      memoryMin: '',
      priceMax: '',
    });
    setSortConfig({ key: '', direction: 'asc' });
  };

  // Check if any local filters are active
  const hasActiveLocalFilters = Object.values(localFilters).some(value => value !== '') || sortConfig.key !== '';

  // Filter and sort GPUs locally
  const processedGpus = useMemo(() => {
    let filtered = [...gpus];

    // Apply local filters
    if (localFilters.provider) {
      filtered = filtered.filter(gpu =>
        gpu.provider.toLowerCase().includes(localFilters.provider.toLowerCase())
      );
    }

    if (localFilters.gpuName) {
      filtered = filtered.filter(gpu =>
        gpu.display_name.toLowerCase().includes(localFilters.gpuName.toLowerCase()) ||
        gpu.provider_instance_id.toLowerCase().includes(localFilters.gpuName.toLowerCase())
      );
    }

    if (localFilters.performance) {
      filtered = filtered.filter(gpu =>
        gpu.performance_category === localFilters.performance
      );
    }

    if (localFilters.memoryMin) {
      const minMemory = parseInt(localFilters.memoryMin);
      filtered = filtered.filter(gpu =>
        gpu.gpu_memory_per_device_gb !== null && gpu.gpu_memory_per_device_gb >= minMemory
      );
    }

    if (localFilters.priceMax) {
      const maxPrice = parseFloat(localFilters.priceMax);
      filtered = filtered.filter(gpu => {
        const price = gpu.total_price_per_hour ?? gpu.on_demand_price_per_hour;
        return price != null && price <= maxPrice;
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = (a as any)[sortConfig.key];
        let bValue = (b as any)[sortConfig.key];

        // Handle special cases
        if (sortConfig.key === 'provider') {
          aValue = a.provider;
          bValue = b.provider;
        } else if (sortConfig.key === 'gpu') {
          aValue = a.display_name;
          bValue = b.display_name;
        } else if (sortConfig.key === 'memory') {
          aValue = a.gpu_memory_per_device_gb || 0;
          bValue = b.gpu_memory_per_device_gb || 0;
        } else if (sortConfig.key === 'price') {
          aValue = a.total_price_per_hour ?? a.on_demand_price_per_hour ?? 999;
          bValue = b.total_price_per_hour ?? b.on_demand_price_per_hour ?? 999;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [gpus, localFilters, sortConfig]);

  // Initial load
  useEffect(() => {
    if (viewMode === 'browse') {
      fetchGPUs();
    }
    fetchCatalogSummary();
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      {/* Header with Stats and Controls */}
      <div className="flex items-center justify-between">
        {catalogSummary && (
          <div className="text-sm text-gray-400">
            {catalogSummary.total_offerings} GPUs available from {Object.keys(catalogSummary.by_provider).length} providers
          </div>
        )}

        {/* Display Controls */}
        {!loading && gpus.length > 0 && viewMode === 'browse' && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setDisplayMode('expanded')}
              className={`p-2 rounded-lg transition-colors ${
                displayMode === 'expanded'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600'
              }`}
              title="Card view"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setDisplayMode('compact')}
              className={`p-2 rounded-lg transition-colors ${
                displayMode === 'compact'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600'
              }`}
              title="Table view"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* View Mode Toggle */}
      <div className="flex bg-gray-800 rounded-lg p-1">
        <button
          onClick={() => setViewMode('browse')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            viewMode === 'browse'
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Browse ({gpus.length})
        </button>
        <button
          onClick={() => setViewMode('recommend')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            viewMode === 'recommend'
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Recommendations
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-600/10 border border-red-600/20 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            {error}
          </div>
        </div>
      )}

      {/* Content based on view mode */}
      {viewMode === 'browse' ? (
        <>
          {/* Loading State */}
          {loading && gpus.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-400">Analyzing GPU catalog...</p>
              </div>
            </div>
          )}

          {/* GPU Grid (Expanded View) */}
          {!loading && gpus.length > 0 && displayMode === 'expanded' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {processedGpus.map(gpu => (
                <SemanticGPUCard
                  key={gpu.id}
                  gpu={gpu}
                  onLaunch={onLaunchGPU}
                  onViewDetails={onViewDetails}
                />
              ))}
            </div>
          )}

          {/* Interactive GPU Table (Compact View) */}
          {!loading && gpus.length > 0 && displayMode === 'compact' && (
            <div ref={containerRef} className="relative bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
              {/* Filter Status Bar */}
              {hasActiveLocalFilters && (
                <div className="px-4 py-2 bg-gray-800/80 border-b border-gray-600 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                    </svg>
                    <span>Showing {processedGpus.length} of {gpus.length} GPUs</span>
                  </div>
                  <button
                    onClick={clearAllLocalFilters}
                    className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800">
                    <tr className="text-left">
                      {/* Provider Column - Clickable + Filter */}
                      <th className={`px-4 py-3 ${localFilters.provider ? 'bg-blue-600/10' : ''}`}>
                        <div className="space-y-2">
                          <button
                            onClick={() => handleSort('provider')}
                            className="flex items-center gap-2 text-xs font-medium text-gray-300 uppercase tracking-wider hover:text-white transition-colors"
                          >
                            Provider
                            {localFilters.provider && (
                              <span className="text-blue-400 text-xs">●</span>
                            )}
                            {sortConfig.key === 'provider' && (
                              <span className="text-blue-400">
                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </button>
                          <select
                            value={localFilters.provider}
                            onChange={(e) => handleLocalFilter('provider', e.target.value)}
                            className={`w-full text-white text-xs rounded px-2 py-1 border ${
                              localFilters.provider
                                ? 'bg-blue-600/20 border-blue-500'
                                : 'bg-gray-700 border-gray-600'
                            }`}
                          >
                            <option value="">All</option>
                            <option value="aws">AWS</option>
                            <option value="gcp">GCP</option>
                            <option value="azure">Azure</option>
                          </select>
                        </div>
                      </th>

                      {/* GPU Name Column - Clickable + Search */}
                      <th className={`px-4 py-3 ${localFilters.gpuName ? 'bg-blue-600/10' : ''}`}>
                        <div className="space-y-2">
                          <button
                            onClick={() => handleSort('gpu')}
                            className="flex items-center gap-2 text-xs font-medium text-gray-300 uppercase tracking-wider hover:text-white transition-colors"
                          >
                            GPU
                            {localFilters.gpuName && (
                              <span className="text-blue-400 text-xs">●</span>
                            )}
                            {sortConfig.key === 'gpu' && (
                              <span className="text-blue-400">
                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </button>
                          <input
                            type="text"
                            placeholder="Search..."
                            value={localFilters.gpuName}
                            onChange={(e) => handleLocalFilter('gpuName', e.target.value)}
                            className={`w-full text-white text-xs rounded px-2 py-1 border placeholder-gray-500 ${
                              localFilters.gpuName
                                ? 'bg-blue-600/20 border-blue-500'
                                : 'bg-gray-700 border-gray-600'
                            }`}
                          />
                        </div>
                      </th>

                      {/* Performance Column - Filter */}
                      <th className={`px-4 py-3 ${localFilters.performance ? 'bg-blue-600/10' : ''}`}>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-xs font-medium text-gray-300 uppercase tracking-wider">
                            Performance
                            {localFilters.performance && (
                              <span className="text-blue-400 text-xs">●</span>
                            )}
                          </div>
                          <select
                            value={localFilters.performance}
                            onChange={(e) => handleLocalFilter('performance', e.target.value)}
                            className={`w-full text-white text-xs rounded px-2 py-1 border ${
                              localFilters.performance
                                ? 'bg-blue-600/20 border-blue-500'
                                : 'bg-gray-700 border-gray-600'
                            }`}
                          >
                            <option value="">All</option>
                            <option value="flagship">Flagship</option>
                            <option value="training_premium">Premium</option>
                            <option value="training_standard">Standard</option>
                            <option value="inference_optimized">Inference</option>
                            <option value="cost_optimized">Budget</option>
                          </select>
                        </div>
                      </th>

                      {/* Memory Column - Clickable + Filter */}
                      <th className={`px-4 py-3 ${localFilters.memoryMin ? 'bg-blue-600/10' : ''}`}>
                        <div className="space-y-2">
                          <button
                            onClick={() => handleSort('memory')}
                            className="flex items-center gap-2 text-xs font-medium text-gray-300 uppercase tracking-wider hover:text-white transition-colors"
                          >
                            Memory
                            {localFilters.memoryMin && (
                              <span className="text-blue-400 text-xs">●</span>
                            )}
                            {sortConfig.key === 'memory' && (
                              <span className="text-blue-400">
                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </button>
                          <select
                            value={localFilters.memoryMin}
                            onChange={(e) => handleLocalFilter('memoryMin', e.target.value)}
                            className={`w-full text-white text-xs rounded px-2 py-1 border ${
                              localFilters.memoryMin
                                ? 'bg-blue-600/20 border-blue-500'
                                : 'bg-gray-700 border-gray-600'
                            }`}
                          >
                            <option value="">Any</option>
                            <option value="8">8 GB+ VRAM</option>
                            <option value="16">16 GB+ VRAM</option>
                            <option value="24">24 GB+ VRAM</option>
                            <option value="40">40 GB+ VRAM</option>
                            <option value="80">80 GB+ VRAM</option>
                          </select>
                        </div>
                      </th>

                      {/* Price Column - Clickable + Filter */}
                      <th className={`px-4 py-3 ${localFilters.priceMax ? 'bg-blue-600/10' : ''}`}>
                        <div className="space-y-2">
                          <button
                            onClick={() => handleSort('price')}
                            className="flex items-center gap-2 text-xs font-medium text-gray-300 uppercase tracking-wider hover:text-white transition-colors"
                          >
                            Price/hr
                            {localFilters.priceMax && (
                              <span className="text-blue-400 text-xs">●</span>
                            )}
                            {sortConfig.key === 'price' && (
                              <span className="text-blue-400">
                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </button>
                          <input
                            type="number"
                            placeholder="Max $"
                            value={localFilters.priceMax}
                            onChange={(e) => handleLocalFilter('priceMax', e.target.value)}
                            className={`w-full text-white text-xs rounded px-2 py-1 border placeholder-gray-500 ${
                              localFilters.priceMax
                                ? 'bg-blue-600/20 border-blue-500'
                                : 'bg-gray-700 border-gray-600'
                            }`}
                            step="0.01"
                          />
                        </div>
                      </th>

                      {/* Action Column - No Filter */}
                      <th className="px-4 py-3">
                        <div className="text-xs font-medium text-gray-300 uppercase tracking-wider">
                          Action
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {processedGpus.map(gpu => (
                      <tr
                        key={gpu.id}
                        className="hover:bg-gray-800/30 transition-colors"
                        onMouseEnter={(e) => {
                          setHoveredGpu({ gpu, rect: e.currentTarget.getBoundingClientRect() });
                        }}
                        onMouseLeave={() => setHoveredGpu(null)}
                      >
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            {getProviderIcon(gpu.provider)}
                            <div>
                              <div className="font-medium text-white">{gpu.provider.toUpperCase()}</div>
                              <div className="text-xs text-gray-500">{gpu.region}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div>
                            <div className="font-medium text-white">{gpu.gpu_count}x {gpu.gpu_model_name}</div>
                            <div className="text-xs text-gray-500">{gpu.provider_instance_id}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              gpu.performance_category === 'flagship' ? 'bg-purple-600/20 text-purple-400' :
                              gpu.performance_category === 'training_premium' ? 'bg-blue-600/20 text-blue-400' :
                              gpu.performance_category === 'training_standard' ? 'bg-green-600/20 text-green-400' :
                              gpu.performance_category === 'inference_optimized' ? 'bg-orange-600/20 text-orange-400' :
                              'bg-gray-600/20 text-gray-400'
                            }`}>
                              {gpu.performance_category.replace(/_/g, ' ')}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="text-white">
                            {gpu.total_gpu_memory_gb || ((gpu.gpu_count || 1) * (gpu.gpu_memory_per_device_gb || 0))} GB VRAM
                          </div>
                          {gpu.system_memory_gib != null && (
                            <div className="text-xs text-gray-500">{gpu.system_memory_gib} GiB RAM</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div>
                            <div className="font-medium text-white">
                              ${gpu.total_price_per_hour?.toFixed(2) ?? gpu.on_demand_price_per_hour?.toFixed(2) ?? 'N/A'}
                            </div>
                            {gpu.disk_price_per_hour != null && (
                              <div className="text-xs text-gray-500">
                                incl. {gpu.default_disk_size_gb}GB disk
                              </div>
                            )}
                            {gpu.spot_price_per_hour != null && (
                              <div className="text-xs text-green-400">
                                Spot: ${gpu.spot_price_per_hour.toFixed(2)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => onLaunchGPU(gpu)}
                            className="px-3 py-1 text-white text-xs rounded font-medium transition-colors bg-blue-600 hover:bg-blue-700"
                          >
                            Launch
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Floating tooltip on row hover */}
              {hoveredGpu && containerRef.current && (() => {
                const containerRect = containerRef.current!.getBoundingClientRect();
                const { gpu, rect } = hoveredGpu;
                const left = rect.left - containerRect.left + rect.width / 2;
                const top = rect.bottom - containerRect.top + 4;

                const costColor =
                  gpu.cost_tier === 'budget' ? 'text-green-400' :
                  gpu.cost_tier === 'medium' ? 'text-yellow-400' :
                  gpu.cost_tier === 'high' ? 'text-orange-400' :
                  gpu.cost_tier === 'premium' ? 'text-red-400' : 'text-gray-400';

                const imageLabel =
                  gpu.image_status === 'custom_image' ? 'Custom image' :
                  gpu.image_status === 'base_provider_image' ? 'Base image' :
                  gpu.image_status === 'no_image' ? 'No image' :
                  gpu.image_status === 'architecture_mismatch' ? 'Arch mismatch' : null;

                return (
                  <div
                    className="absolute z-50 pointer-events-none"
                    style={{ left, top, transform: 'translateX(-50%)' }}
                  >
                    {/* Arrow */}
                    <div className="flex justify-center">
                      <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-gray-700" />
                    </div>
                    {/* Body */}
                    <div className="bg-gray-950 border border-gray-700 rounded-lg shadow-xl px-4 py-3 min-w-[280px] max-w-[360px]">
                      {/* Title */}
                      <div className="mb-2 pb-2 border-b border-gray-800">
                        <div className="text-white text-sm font-medium">
                          {gpu.gpu_count}x {gpu.gpu_model_name}
                        </div>
                        <div className="text-gray-500 text-xs">{gpu.provider_instance_id}</div>
                      </div>

                      {/* Specs grid */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs mb-2">
                        {gpu.gpu_architecture && (
                          <>
                            <span className="text-gray-500">Architecture</span>
                            <span className="text-gray-300">{gpu.gpu_architecture}</span>
                          </>
                        )}
                        {gpu.fp32_tflops != null && (
                          <>
                            <span className="text-gray-500">FP32 TFLOPS</span>
                            <span className="text-gray-300">{gpu.fp32_tflops}</span>
                          </>
                        )}
                        {gpu.gpu_count != null && gpu.gpu_count > 1 && gpu.gpu_memory_per_device_gb != null && (
                          <>
                            <span className="text-gray-500">Per-GPU Memory</span>
                            <span className="text-gray-300">{gpu.gpu_memory_per_device_gb} GB</span>
                          </>
                        )}
                        <>
                          <span className="text-gray-500">Total VRAM</span>
                          <span className="text-gray-300">{gpu.total_gpu_memory_gb || ((gpu.gpu_count || 1) * (gpu.gpu_memory_per_device_gb || 0))} GB</span>
                        </>
                        {gpu.system_memory_gib != null && (
                          <>
                            <span className="text-gray-500">System RAM</span>
                            <span className="text-gray-300">{gpu.system_memory_gib} GiB</span>
                          </>
                        )}
                        {gpu.vcpus != null && (
                          <>
                            <span className="text-gray-500">vCPUs</span>
                            <span className="text-gray-300">{gpu.vcpus}</span>
                          </>
                        )}
                        {gpu.network_bandwidth_gbps != null && (
                          <>
                            <span className="text-gray-500">Network</span>
                            <span className="text-gray-300">{gpu.network_bandwidth_gbps} Gbps</span>
                          </>
                        )}
                      </div>

                      {/* Footer */}
                      <div className="pt-2 border-t border-gray-800 flex items-center gap-3 text-xs">
                        <span className={`capitalize ${costColor}`}>{gpu.cost_tier}</span>
                        <span className="text-gray-500">Score {Math.round(gpu.performance_score)}</span>
                        {imageLabel && (
                          <span className="text-gray-500">{imageLabel}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Empty State */}
          {!loading && gpus.length === 0 && (
            <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700">
              <svg className="w-16 h-16 text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <h3 className="text-lg font-medium text-gray-300 mb-2">No GPUs Found</h3>
              <p className="text-gray-500">
                Try adjusting your filters or check back later for updated catalog data.
              </p>
            </div>
          )}

          {/* Request a GPU Type */}
          {!loading && (
            <div className="flex justify-center pt-2">
              <button
                onClick={() => setShowQuotaRequestModal(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white bg-gray-800/50 hover:bg-gray-700 border border-gray-700 hover:border-gray-600 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Request a GPU Type
              </button>
            </div>
          )}

          {/* Quota Request Modal (generic — no pre-selected GPU) */}
          <QuotaRequestModal
            isOpen={showQuotaRequestModal}
            onClose={() => setShowQuotaRequestModal(false)}
            gpu={{
              provider: '',
              provider_instance_id: '',
              display_name: '',
              region: '',
            }}
          />
        </>
      ) : (
        /* Recommendations View */
        <GPURecommendations
          onGetRecommendations={handleGetRecommendations}
          recommendations={recommendations}
          loading={recommendationLoading}
          onLaunchGPU={onLaunchGPU}
          onViewDetails={onViewDetails}
        />
      )}
    </div>
  );
}
